import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { getDevice } from '@panel/commons/helpers';
import { useQuery } from '@panel/commons/hooks';
import {
  Container,
  InputContainer,
  Label,
  Counter,
  InputFooterContainer,
  ButtonAI,
  ConfirmedTextAI,
  IconAI,
  Icons,
  AssistTextHint
} from './styles';
import AutoResizingTextArea from './AutoResizingTextArea';
import AutoResizingInputCode from './AutoResizingInputCode';
import AutoResizingInputTitle from './AutoResizingInputTitle';
import ErrorMessages from './ErrorMessages';
import { normalizeInputOnBlur } from './normalizeInputOnBlur';
import LoaderAI from './LoaderAI';
import handleLimitedInputChange from './handleLimitedInputChange';
import { removeSpecialCharsAndEmojis } from './cleanText';
import { sendIATrackingInfo } from '../../../utils/adaptStepDataAndSendTrackinInfo';
import { getStepMainSelectors } from '../../../ducks/propertyType';

export const DescriptionSection = ({
  register,
  setValue,
  setTitleError,
  setDescriptionError,
  setInternalCodeError,
  titleError,
  descriptionError,
  internalCodeError,
  title,
  description,
  code,
  titleAI,
  descriptionAI,
  suggestedDataLoading,
  suggestedDataError,
  handleSuggester = () => {}
}) => {
  const { t } = useTranslation();
  const device = getDevice();
  const mainData = useSelector(getStepMainSelectors);
  const { propertyTypeName, operationType, propertySubTypeName } = mainData.formValues;
  const [postingIdFromUrl, isEdition, isDraftFromPanel] = useQuery(useLocation(), [
    'postingId',
    'isEdition',
    'isDraftFromPanel'
  ]);

  // Estados para title
  const [titleIsLoading, setTitleIsLoading] = useState(false);
  const [titleIsUsingAI, setTitleIsUsingAI] = useState(false);
  const [titleOriginalText, setTitleOriginalText] = useState('');
  const [titleAIGeneratedText, setTitleAIGeneratedText] = useState('');
  const [titleTriggeredFetch, setTitleTriggeredFetch] = useState(false);
  // Estados para description
  const [descriptionIsLoading, setDescriptionIsLoading] = useState(false);
  const [descriptionIsUsingAI, setDescriptionIsUsingAI] = useState(false);
  const [descriptionOriginalText, setDescriptionOriginalText] = useState('');
  const [descriptionAIGeneratedText, setDescriptionAIGeneratedText] = useState('');
  const [descriptionTriggeredFetch, setDescriptionTriggeredFetch] = useState(false);
  // Variables de loading
  const isTitleLoading = titleIsLoading || (suggestedDataLoading && titleTriggeredFetch);
  const isDescriptionLoading = descriptionIsLoading || (suggestedDataLoading && descriptionTriggeredFetch);
  // Variables para visualizar los contadores de caracteres
  const getTitleTextLength = isTitleLoading ? titleOriginalText?.length || 0 : title?.length;
  const getDescriptionTextLength = isDescriptionLoading ? descriptionOriginalText?.length || 0 : description?.length;
  // Variables para establecer los length máximos de los inputs
  const config = {
    maxLength: { title: 100, description: 10000, code: 51 },
    minLength: { title: 10, description: 150 }
  };

  // Objeto global de setters de estado
  const fieldSetters = {
    title: {
      setOriginalText: setTitleOriginalText,
      setIsLoading: setTitleIsLoading,
      setTriggeredFetch: setTitleTriggeredFetch,
      setAIGeneratedText: setTitleAIGeneratedText,
      setIsUsingAI: setTitleIsUsingAI
    },
    description: {
      setOriginalText: setDescriptionOriginalText,
      setIsLoading: setDescriptionIsLoading,
      setTriggeredFetch: setDescriptionTriggeredFetch,
      setAIGeneratedText: setDescriptionAIGeneratedText,
      setIsUsingAI: setDescriptionIsUsingAI
    }
  };

  // Función para enviar tracking de IA
  const sendAITracking = (fieldType, action) => {
    sendIATrackingInfo(
      fieldType === 'title' ? 'Título' : 'Descripción',
      action,
      postingIdFromUrl,
      isDraftFromPanel,
      isEdition,
      operationType,
      propertyTypeName,
      propertySubTypeName
    );
  };

  // Función genérica para generar la sugerencia de la IA
  const generateAI = (fieldType, originalText, aiGeneratedText, event) => {
    const setters = fieldSetters[fieldType];
    // Guardamos el texto original antes de generar IA
    setters.setOriginalText(originalText);
    setters.setIsLoading(true);

    if (aiGeneratedText) {
      // Si ya tenemos datos de IA, aplicarlos directamente
      processAIResponse(aiGeneratedText, fieldType);
    } else {
      setters.setTriggeredFetch(true); // Si no hay datos, marcamos que este campo disparó el fetch
      handleSuggester(event); // Hacemos la petición de sugerencias de IA al backend
    }

    sendAITracking(fieldType, 'Generar');
  };

  // Función genérica para procesar la respuesta de IA
  const processAIResponse = (aiGeneratedText, fieldType) => {
    if (!aiGeneratedText) return;

    const cleanContent = removeSpecialCharsAndEmojis(aiGeneratedText).trim();
    const setters = fieldSetters[fieldType];
    // Actualizamos el campo con el contenido de IA
    setters.setAIGeneratedText(cleanContent);
    setters.setIsUsingAI(true);
    setValue(fieldType, cleanContent, { shouldDirty: true });
    // limpiamos errores
    if (fieldType === 'title') {
      setTitleError(false);
    } else if (fieldType === 'description') {
      setDescriptionError(false);
    }

    // Limpiamos los estados de loading y triggeredFetch
    setTimeout(() => {
      setters.setIsLoading(false);
      setters.setTriggeredFetch(false);
    }, 500);
  };

  // Función genérica para deshacer la sugerencia de IA
  const undoAI = (fieldType, originalText, isAIEdited) => {
    const setters = {
      title: setTitleIsUsingAI,
      description: setDescriptionIsUsingAI
    };
    // Si el usuario editó el texto generado por IA (isAIEdited=true), solo ocultamos el tag 'Generado con IA'
    if (isAIEdited) {
      setters[fieldType](false);
      return;
    }
    // Restauramos el texto original sólamente al hacer clic en "Deshacer"
    setValue(fieldType, originalText, { shouldDirty: true });
    setters[fieldType](false);

    sendAITracking(fieldType, 'Deshacer');
  };

  // Función genérica para manejar los cambios en los inputs con IA
  const handleAIInputOnChange = (e, fieldType, isUsingAI, aiGeneratedText, originalText, setError, maxLength) => {
    // Solo verificamos edición de IA si no hay composición activa.
    if (isUsingAI && aiGeneratedText && !e.isComposing) {
      const currentText = e.target.value;
      const normalize = text => text.replace(/\s+/g, ' ').trim(); // Normalizamos los espacios en blanco y salto de línea
      const hasEdited = normalize(currentText) !== normalize(aiGeneratedText);
      // Si el usuario edita el texto generado por IA, ocultamos el tag de 'Generado por IA'
      if (hasEdited) {
        undoAI(fieldType, originalText, true);
      }
    }
    // Si no se está usando IA, o no hay texto generado, manejamos el cambio normalmente
    handleLimitedInputChange({
      event: e,
      setValue,
      setError,
      maxLength,
      fieldName: fieldType
    });
  };

  // Procesamos la respuesta de IA para title - Sólo si title disparó el fetch
  useEffect(() => {
    if (!suggestedDataLoading && titleIsLoading && titleTriggeredFetch) {
      processAIResponse(titleAI, 'title');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestedDataLoading, titleIsLoading, titleTriggeredFetch]);

  // Procesamos la respuesta de IA para description - Sólo si description disparó el fetch
  useEffect(() => {
    if (!suggestedDataLoading && descriptionIsLoading && descriptionTriggeredFetch) {
      processAIResponse(descriptionAI, 'description');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestedDataLoading, descriptionIsLoading, descriptionTriggeredFetch]);

  // Auto-hide error messages después de 2 segundos
  useEffect(() => {
    const timeouts = [];
    if (titleError === 'maxLength') {
      timeouts.push(setTimeout(() => setTitleError(false), 2000));
    }
    if (descriptionError === 'maxLength') {
      timeouts.push(setTimeout(() => setDescriptionError(false), 2000));
    }
    return () => timeouts.forEach(clearTimeout);
  }, [titleError, descriptionError, setTitleError, setDescriptionError]);

  useEffect(() => {
    // Si hay un error en el fetch de sugerencias de IA, reseteamos los estados de loading y triggeredFetch
    if (suggestedDataError) {
      setTimeout(() => {
        setTitleIsLoading(false);
        setDescriptionIsLoading(false);
        setTitleTriggeredFetch(false);
        setDescriptionTriggeredFetch(false);
      }, 300);
    }
  }, [suggestedDataError]);

  return (
    <Container data-publi='descriptionStep-descriptionSection-container'>
      {/* TITLE SECTION */}
      <InputContainer>
        <div className='input-labels'>
          <Label data-publi='descriptionStep-title-label'>{t('professionalPubli.description.title.label')}</Label>
          {!isTitleLoading && (
            <ButtonAI
              device={device}
              data-publi='descriptionStep-title-cta'
              onClick={e => {
                e.preventDefault();
                if (titleIsUsingAI) {
                  undoAI('title', titleOriginalText); // Si está mostrando IA, deshacer
                } else {
                  generateAI('title', title, titleAI, e); // Si no está mostrando IA, generar
                }
              }}
            >
              {t(`professionalPubli.description.AI.${titleIsUsingAI ? 'undo' : 'generate'}`)}
              <IconAI
                src={Icons[titleIsUsingAI ? 'undo' : 'generate']}
                alt='Generar IA-title'
                data-publi='descriptionStep-title-IconCta'
              />
            </ButtonAI>
          )}
        </div>
        {isTitleLoading ? (
          <LoaderAI />
        ) : (
          <AutoResizingInputTitle
            name='title'
            value={title}
            error={titleError}
            onBlur={() => normalizeInputOnBlur({ value: title, setValue, fieldName: 'title' })}
            onChange={e => {
              handleAIInputOnChange(
                e,
                'title',
                titleIsUsingAI,
                titleAIGeneratedText,
                titleOriginalText,
                setTitleError,
                config.maxLength.title
              );
            }}
            onCompositionEnd={e => {
              // Cuando termina la composición (unión de caracteres acentuados), limpiamos el texto
              const cleanedValue = removeSpecialCharsAndEmojis(e.target.value);
              if (cleanedValue !== e.target.value) {
                setValue('title', cleanedValue, { shouldDirty: true });
              }
            }}
            {...register('title', {
              required: true,
              minLength: config.minLength.title,
              maxLength: config.maxLength.title
            })}
            placeholder={t('professionalPubli.description.title.placeholder')}
          />
        )}
        <InputFooterContainer>
          <div>
            {titleIsUsingAI && !isTitleLoading && (
              <ConfirmedTextAI className='ai-generated-text' data-publi='descriptionStep-title-AI-generated'>
                <IconAI src={Icons.stars} height='16px' width='16px' alt='Generated IA-title' />
                {t('professionalPubli.description.AI.generated')}
              </ConfirmedTextAI>
            )}
            {titleError && <ErrorMessages titleError={titleError} />}
          </div>
          <Counter data-publi='descriptionStep-title-counter' error={titleError}>
            {getTitleTextLength}
            {t('professionalPubli.description.title.maxLength')}
          </Counter>
        </InputFooterContainer>
      </InputContainer>

      {/* DESCRIPTION SECTION */}
      <InputContainer>
        <div className='input-labels'>
          <Label data-publi='descriptionStep-description-label'>
            {t('professionalPubli.description.textDescription.label')}
          </Label>
          {!isDescriptionLoading && (
            <ButtonAI
              device={device}
              data-publi='descriptionStep-description-cta'
              onClick={e => {
                e.preventDefault();
                if (descriptionIsUsingAI) {
                  undoAI('description', descriptionOriginalText); // Si está mostrando IA, deshacer
                } else {
                  generateAI('description', description, descriptionAI, e); // Si no está mostrando IA, generar
                }
              }}
            >
              {t(`professionalPubli.description.AI.${descriptionIsUsingAI ? 'undo' : 'generate'}`)}
              <IconAI
                src={Icons[descriptionIsUsingAI ? 'undo' : 'generate']}
                alt='Generar IA-descripcion'
                data-publi='descriptionStep-description-IconCta'
              />
            </ButtonAI>
          )}
        </div>
        {isDescriptionLoading ? (
          <LoaderAI height={device === 'mobile' ? '88px' : '100px'} />
        ) : (
          <AutoResizingTextArea
            name='description'
            value={description}
            error={descriptionError}
            onBlur={() => normalizeInputOnBlur({ value: description, setValue, fieldName: 'description' })}
            onChange={e => {
              handleAIInputOnChange(
                e,
                'description',
                descriptionIsUsingAI,
                descriptionAIGeneratedText,
                descriptionOriginalText,
                setDescriptionError,
                config.maxLength.description
              );
            }}
            onCompositionEnd={e => {
              const cleanedValue = removeSpecialCharsAndEmojis(e.target.value);
              if (cleanedValue !== e.target.value) {
                setValue('description', cleanedValue, { shouldDirty: true });
              }
            }}
            {...register('description', {
              required: true,
              minLength: config.minLength.description,
              maxLength: config.maxLength.description
            })}
            placeholder={t('professionalPubli.description.textDescription.placeholder')}
            isDesktop={device === 'desktop'}
          />
        )}
        <InputFooterContainer>
          <AssistTextHint>
            {descriptionIsUsingAI && !isDescriptionLoading && (
              <ConfirmedTextAI data-publi='descriptionStep-description-AI-generated' className='ai-generated-text'>
                <IconAI src={Icons.stars} height='16px' width='16px' alt='Generated IA-description' />
                {t('professionalPubli.description.AI.generated')}
              </ConfirmedTextAI>
            )}
            {descriptionError && <ErrorMessages descriptionError={descriptionError} />}
          </AssistTextHint>
          <Counter data-publi='descriptionStep-description-counter' error={descriptionError}>
            {getDescriptionTextLength}
            {t('professionalPubli.description.textDescription.maxLength')}
          </Counter>
        </InputFooterContainer>
      </InputContainer>

      {/* CODE SECTION */}
      <InputContainer>
        <Label data-publi='descriptionStep-code-label'>{t('professionalPubli.description.code.label')}</Label>
        <AutoResizingInputCode
          name='code'
          value={code}
          error={internalCodeError}
          onChange={e => {
            const newValue = e.target.value.slice(0, config.maxLength.code);
            setValue('code', newValue);
            setInternalCodeError(e.target.value.length >= config.maxLength.code ? 'maxLength' : false);
          }}
          {...register('code', { maxLength: config.maxLength.code })}
          placeholder={t('professionalPubli.description.code.placeholder')}
          device={device}
        />
        <InputFooterContainer>
          {internalCodeError && <ErrorMessages internalCodeError={internalCodeError} />}
        </InputFooterContainer>
      </InputContainer>
    </Container>
  );
};
